import {DownloadOutlined, FileTextOutlined} from '@ant-design/icons';
import {Button, Divider, Modal, message} from 'antd';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';
import React, {useRef, useState} from 'react';

import Collapselogo from '../../../../../Assets/CollapsedLogo.png';
import logo from '../../../../../Assets/logo.png';

// ─── Constants 
const CLINIC = {
  name: 'VedaNova',
  tagline: 'Hope  •  Healing  •  Happiness',
  address: 'VedaNova Pvt. Ltd FF15, Landmark Business Centre, Satara - 415001',
  phone: '+91 90281 56566',
  website: 'www.vedanova.com',
  email: 'contact@vedanova.com',
  social: 'vedanova.ayurmed',
};

const PRIMARY = '#2f6b5e';
const GOLD = '#b69c66';

export const fmtDate = v => (v ? dayjs(v).format('DD MMM YYYY') : '-');
export const fmtCurrency = v => (v || v === 0) ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

const PrescriptionTemplate = ({prescription, patient, consultantName, followUp, printRef}) => {
  const medicines = prescription?.medicines ?? [];
  const dietAdvice = prescription?.dietAdvice ?? [];
  const panchakarmaAdvice = prescription?.panchakarmaAdvice ?? [];

  // Prefer the stored total; fall back to summing selling price × quantity
  // per medicine in case an older record doesn't have totalMedicinePrice saved.
  const totalMedicinePrice = prescription?.totalMedicinePrice
    ?? medicines.reduce((sum, m) => sum + ((Number(m.quantity) || 1) * (Number(m.sellingPrice) || 0)), 0);

  return (
    <div
      ref={printRef}
      style={{
        fontFamily: '\'Poppins\', \'Segoe UI\', sans-serif',
        background: '#fdfaf4',
        color: '#1f2a28',
        width: '210mm',
        minHeight: '297mm',
        boxSizing: 'border-box',
        padding: '10mm 12mm',
        position: 'relative',
        border: `1px solid ${GOLD}55`,
      }}
    >
      {/* ── Header ── */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '6px'}}>
        <tbody>
          <tr>
            <td style={{verticalAlign: 'top', width: '45%'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <img src={logo} alt={'Logo'} className={'h-10'} />
              </div>
            </td>
            <td style={{verticalAlign: 'top', width: '55%', textAlign: 'right', fontSize: 10}}>
              <div style={{marginBottom: 4}}>
                <strong style={{color: PRIMARY}}>📍 </strong>
                {CLINIC.address}
              </div>
              <div>
                <strong style={{color: PRIMARY}}>📞 </strong>
                {CLINIC.phone}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{height: 2, background: `linear-gradient(90deg, ${PRIMARY}, ${GOLD})`, marginBottom: '10px'}} />

      {/* ── Patient Info ── */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '10px', fontSize: 11}}>
        <tbody>
          <tr>
            <td style={{padding: '3px 0', width: '55%'}}>
              <strong>Patient Name : </strong>
              {patient?.name || prescription?.patientId}
            </td>
            <td style={{padding: '3px 0', width: '45%'}}>
              <strong>Date : </strong>
              {fmtDate(prescription?.createdDate)}
            </td>
          </tr>
          <tr>
            <td style={{padding: '3px 0'}}>
              <strong>Consultant : </strong>
              <span style={{borderBottom: `1px solid ${PRIMARY}55`}}>{consultantName || '-'}</span>
            </td>
            <td style={{padding: '3px 0'}}>
              <strong>Age/Gender : </strong>
              {patient?.age ? `${patient.age} yrs` : '-'}{patient?.gender ? ` / ${patient.gender}` : ''}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{borderBottom: `1px solid ${GOLD}55`, marginBottom: '6px'}} />

      {/* ── Rx symbol ── */}
      <div style={{fontSize: 30, color: PRIMARY, fontWeight: 700, marginBottom: '6px'}}>℞</div>

      {/* ── Medicines ── */}
      <div style={{marginBottom: '14px'}}>
        <div style={{fontSize: 12, fontWeight: 700, color: PRIMARY, marginBottom: 4}}>Medicines</div>
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 10}}>
          <thead>
            <tr style={{background: `${PRIMARY}14`}}>
              {['#', 'Medicine', 'Dosage', 'Frequency', 'Duration', 'Before/After Food', 'Qty', 'Route', 'Instructions', 'Price (₹)'].map(h => (
                <th
                  key={h}
                  style={{border: `1px solid ${PRIMARY}33`, padding: '4px 5px', textAlign: 'left', color: PRIMARY}}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {medicines.length ? medicines.map((m, idx) => (
              <tr key={idx}>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{idx + 1}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px', fontWeight: 600}}>{m.medicineName || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.dosage || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.frequency || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.duration || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.beforeAfterFood || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.quantity ?? '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.route || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{m.instructions || '-'}</td>
                <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{fmtCurrency(m.sellingPrice)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={10} style={{border: `1px solid ${PRIMARY}22`, padding: '8px', textAlign: 'center', color: '#999'}}>
                  No medicines prescribed
                </td>
              </tr>
            )}
          </tbody>
          {!!medicines.length && (
            <tfoot>
              <tr>
                <td
                  colSpan={9}
                  style={{
                    border: `1px solid ${PRIMARY}33`,
                    padding: '5px 5px',
                    textAlign: 'right',
                    fontWeight: 700,
                    color: PRIMARY,
                    background: `${PRIMARY}0d`,
                  }}
                >
                  Total Medicine Price
                </td>
                <td
                  style={{
                    border: `1px solid ${PRIMARY}33`,
                    padding: '5px 5px',
                    fontWeight: 700,
                    color: PRIMARY,
                    background: `${PRIMARY}0d`,
                  }}
                >
                  {fmtCurrency(totalMedicinePrice)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Diet Advice ── */}
      {!!dietAdvice.length && (
        <div style={{marginBottom: '14px'}}>
          <div style={{fontSize: 12, fontWeight: 700, color: PRIMARY, marginBottom: 4}}>Diet Advice</div>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 10}}>
            <thead>
              <tr style={{background: `${GOLD}22`}}>
                {['Meal Type', 'Recommended Food', 'Avoid Food', 'Advice'].map(h => (
                  <th key={h} style={{border: `1px solid ${GOLD}55`, padding: '4px 5px', textAlign: 'left', color: '#8a6d2f'}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dietAdvice.map((d, idx) => (
                <tr key={idx}>
                  <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{d.mealType || '-'}</td>
                  <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{d.recommendedFood || '-'}</td>
                  <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{d.avoidFood || '-'}</td>
                  <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{d.advice || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Panchakarma Advice ── */}
      {!!panchakarmaAdvice.length && (
        <div style={{marginBottom: '14px'}}>
          <div style={{fontSize: 12, fontWeight: 700, color: PRIMARY, marginBottom: 4}}>Panchakarma Advice</div>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 10}}>
            <thead>
              <tr style={{background: `${PRIMARY}14`}}>
                {['Therapy', 'No. of Days', 'Session Time', 'Instructions'].map(h => (
                  <th key={h} style={{border: `1px solid ${PRIMARY}33`, padding: '4px 5px', textAlign: 'left', color: PRIMARY}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {panchakarmaAdvice.map((p, idx) => (
                <tr key={idx}>
                  <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{p.therapyName || '-'}</td>
                  <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{p.numberOfDays ?? '-'}</td>
                  <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{p.sessionTime || '-'}</td>
                  <td style={{border: `1px solid ${PRIMARY}22`, padding: '4px 5px'}}>{p.instructions || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Next Follow Up ── */}
      {followUp && (
        <div style={{marginBottom: '14px'}}>
          <div style={{fontSize: 12, fontWeight: 700, color: PRIMARY, marginBottom: 4}}>Next Follow Up</div>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 10}}>
            <thead>
              <tr style={{background: `${GOLD}22`}}>
                {['Previous Visit', 'Follow Up Days', 'Next Visit', 'Remarks'].map(h => (
                  <th key={h} style={{border: `1px solid ${GOLD}55`, padding: '4px 5px', textAlign: 'left', color: '#8a6d2f'}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{fmtDate(followUp.previousVisit) || '-'}</td>
                <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{followUp.followUpDays ?? '-'}</td>
                <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px', fontWeight: 600}}>{fmtDate(followUp.nextVisit) || '-'}</td>
                <td style={{border: `1px solid ${GOLD}33`, padding: '4px 5px'}}>{followUp.remarks || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── Doctor Notes ── */}
      {prescription?.doctorNotes && (
        <div style={{marginBottom: '14px', fontSize: 11}}>
          <strong style={{color: PRIMARY}}>Doctor Notes: </strong>
          {prescription.doctorNotes}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{position: 'absolute', bottom: '14mm', left: '12mm', right: '12mm'}}>
        <div style={{textAlign: 'center', fontSize: 10, fontStyle: 'italic', color: GOLD, marginBottom: 6}}>
          Thank you for choosing natural healing, we wish you health and happiness
        </div>
        <div className={'flex flex-row justify-center items-center text-center'}>
          <Divider className={'w-1/2 bg-primary-100'} />
          <img src={Collapselogo} alt={'Logo'} className={'md:h-14 h-8'} />
          <Divider className={'w-1/2 bg-primary-100'} />
        </div>
        <div style={{textAlign: 'center', fontSize: 9, color: '#555'}}>
          🌐 {CLINIC.website} &nbsp;|&nbsp; ✉ {CLINIC.email} &nbsp;|&nbsp; {CLINIC.social}
        </div>
      </div>
    </div>
  );
};

PrescriptionTemplate.propTypes = {
  prescription: PropTypes.object.isRequired,
  patient: PropTypes.object,
  consultantName: PropTypes.string,
  followUp: PropTypes.object,
  printRef: PropTypes.object.isRequired,
};

// ─── Preview + Download Modal (exported, reusable) ─────────────────────────────
// `consultantName` is now derived primarily from the prescription itself
// (prescription.doctorName, populated by the API from the saved doctorId).
// A `consultantName` prop is still accepted as a fallback for cases where the
// prescription payload doesn't include a doctor name yet.
export const DownloadPrescription = ({open, onClose, prescription, patient, consultantName, followUp}) => {
  const printRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!prescription) {return null;}

  const fileTag = patient?.name || prescription.patientId || 'prescription';

  const resolvedConsultantName = prescription?.doctorName
    || prescription?.doctor?.fullName
    || consultantName;

  const handleDownload = async () => {
    if (!printRef.current) {return;}
    setDownloading(true);
    try {
      await html2pdf()
        .set({
          margin: 0,
          filename: `Prescription-${fileTag.toString().replace(/\s+/g, '-')}.pdf`,
          image: {type: 'jpeg', quality: 0.98},
          html2canvas: {scale: 2, useCORS: true, logging: false},
          jsPDF: {unit: 'mm', format: 'a4', orientation: 'portrait'},
        })
        .from(printRef.current)
        .save();
      message.success('Prescription PDF downloaded!');
      onClose();
    } catch {
      message.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={860}
      title={(
        <div className={'flex items-center gap-2'}>
          <FileTextOutlined className={'text-primary-500'} />
          <span className={'font-bold text-gray-800'}>
            Prescription Preview —{' '}
            <span className={'font-mono text-primary-600'}>{fileTag}</span>
          </span>
        </div>
      )}
      styles={{body: {maxHeight: '80vh', overflowY: 'auto', padding: '16px 20px', background: '#f5f5f5'}}}
      footer={(
        <div className={'flex justify-end gap-3 py-1'}>
          <Button onClick={onClose}>Close</Button>
          <Button
            type={'primary'}
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
            className={'btn-primary'}
          >
            Download Prescription PDF
          </Button>
        </div>
      )}
    >
      <div
        className={'border border-primary-200 h-4 rounded-lg overflow-hidden shadow-sm bg-white mx-auto'}
        style={{width: 'fit-content', transform: 'scale(0.82)', transformOrigin: 'top center'}}
      >
        <PrescriptionTemplate
          prescription={prescription}
          patient={patient}
          consultantName={resolvedConsultantName}
          followUp={followUp}
          printRef={printRef}
        />
      </div>
    </Modal>
  );
};

DownloadPrescription.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prescription: PropTypes.object,
  patient: PropTypes.object,
  consultantName: PropTypes.string,
  followUp: PropTypes.object,
};

export default DownloadPrescription;
